/**
 * `ProductsImportModal` — TH-55367.
 *
 * Modal de importação em lote para a tela /products. Aceita .csv e .xlsx;
 * delega parsing/validação ao backend (POST /products/bulk-import — TH-55366).
 *
 * Sem preview client-side por opção arquitetural (drop do AC original):
 * backend devolve `errors[]` anotados com row_number + reason, o que dá ao
 * operador o mesmo poder de diagnóstico sem custo de 350KB de papaparse +
 * sheetjs no admin. Ver comentário no Hub TH-55367.
 *
 * Fluxo:
 *   1. Usuário escolhe arquivo (drag-and-drop ou clique).
 *   2. Validação client-side só de mime/size (feedback imediato sem round-trip).
 *   3. Submit → mutation.mutate(file).
 *   4. Após resposta: estado interno vira `result` e renderiza o resumo
 *      (contadores + tabela de erros). Botão Fechar dispara `onImported`.
 *
 * Padrão visual espelha CustomArtUploadModal (drag-and-drop + tokens DS).
 */

import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Upload, X as XIcon } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";
import {
  useImportProducts,
  type BulkImportResponse,
} from "@/hooks/useImportProducts";

// MIME types que o backend (parser.parse_file) aceita. .csv text/plain é
// comum em uploads do Safari; manter como aceito reduz pegadinha do usuário.
const ALLOWED_MIMES = [
  "text/csv",
  "application/vnd.ms-excel", // alguns browsers mandam isso pra .csv ou .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "", // input file sem mime detectado
] as const;
const ALLOWED_EXTS = [".csv", ".xlsx"] as const;
const MAX_BYTES = 5 * 1024 * 1024;

function hasAllowedExt(name: string): boolean {
  const lower = name.toLowerCase();
  return ALLOWED_EXTS.some((ext) => lower.endsWith(ext));
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export interface ProductsImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Disparado quando o operador fecha o modal após uma importação bem-sucedida.
   * Componente pai decide invalidar cache de products. */
  onImported?: (response: BulkImportResponse) => void;
}

export function ProductsImportModal({
  open,
  onOpenChange,
  onImported,
}: ProductsImportModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResponse | null>(null);

  const mutation = useImportProducts();

  // Reset completo ao fechar — abrir de novo começa virgem.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileError(null);
      setSubmitError(null);
      setResult(null);
      mutation.reset();
    }
  }, [open, mutation]);

  function handleFile(selected: File) {
    setFileError(null);
    setSubmitError(null);
    setResult(null);

    const mimeOk =
      (ALLOWED_MIMES as ReadonlyArray<string>).includes(selected.type) ||
      hasAllowedExt(selected.name);
    if (!mimeOk) {
      setFileError(
        `Formato '${selected.type || "desconhecido"}' não aceito — use .csv ou .xlsx.`,
      );
      setFile(selected);
      return;
    }
    if (selected.size === 0) {
      setFileError("Arquivo vazio.");
      setFile(selected);
      return;
    }
    if (selected.size > MAX_BYTES) {
      setFileError(
        `Arquivo tem ${formatBytes(selected.size)} — máximo permitido é 5 MB.`,
      );
      setFile(selected);
      return;
    }
    setFile(selected);
  }

  function onPickClick() {
    inputRef.current?.click();
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  async function onSubmit() {
    setSubmitError(null);
    if (!file) {
      setSubmitError("Escolha um arquivo antes de importar.");
      return;
    }
    if (fileError) {
      setSubmitError("Corrija o erro do arquivo antes de importar.");
      return;
    }
    try {
      const response = await mutation.mutateAsync(file);
      setResult(response);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Falha ao importar.");
    }
  }

  function onClose() {
    if (result && onImported) onImported(result);
    onOpenChange(false);
  }

  // ---------- render: resultado ----------
  if (result) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogTitle>Importação concluída</DialogTitle>
          <DialogDescription>
            {file?.name ?? "planilha"} processada — confira o resumo abaixo.
          </DialogDescription>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <StatCard label="Criados" value={result.created} tone="success" />
            <StatCard label="Atualizados" value={result.updated} tone="info" />
            <StatCard label="Inalterados" value={result.skipped} tone="muted" />
          </div>

          {result.errors.length > 0 ? (
            <div className="mt-4">
              <p className="mb-2 text-sm font-medium text-[var(--color-foreground)]">
                {result.errors.length}{" "}
                {result.errors.length === 1 ? "linha rejeitada" : "linhas rejeitadas"}
              </p>
              <div className="max-h-64 overflow-auto rounded-md border border-[var(--color-border)]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[var(--color-card)] text-left text-[var(--color-muted-foreground)]">
                    <tr>
                      <th className="px-3 py-2 font-medium">Linha</th>
                      <th className="px-3 py-2 font-medium">EAN</th>
                      <th className="px-3 py-2 font-medium">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr
                        key={`${e.row_number}-${i}`}
                        className="border-t border-[var(--color-border)]"
                      >
                        <td className="px-3 py-2 font-mono tabular-nums text-[var(--color-muted-foreground)]">
                          {e.row_number}
                        </td>
                        <td className="px-3 py-2 font-mono tabular-nums text-[var(--color-muted-foreground)]">
                          {e.ean ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-[var(--color-destructive)]">
                          {e.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                Corrija essas linhas na planilha original e suba novamente — a
                importação é idempotente, então linhas já cadastradas não viram
                duplicata.
              </p>
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-2 rounded-md border border-[var(--color-success)] bg-[var(--color-success)]/10 px-3 py-2 text-sm text-[var(--color-success)]">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Nenhuma linha rejeitada.
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <Button onClick={onClose} data-testid="products-import-close-result">
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---------- render: upload ----------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogTitle>Importar produtos</DialogTitle>
        <DialogDescription>
          Suba uma planilha .csv ou .xlsx com colunas <strong>EAN</strong> e{" "}
          <strong>Nome</strong> (ou <strong>Descrição</strong>). Importação é
          idempotente — rodar duas vezes não cria duplicatas.
        </DialogDescription>

        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={cn(
            "mt-4 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 transition-colors",
            dragOver
              ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
              : "border-[var(--color-border)] bg-[var(--color-background)]",
          )}
        >
          {file ? (
            <div className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
              <span className="font-medium">{file.name}</span>
              <span className="font-mono tabular-nums text-[var(--color-muted-foreground)]">
                ({formatBytes(file.size)})
              </span>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setFileError(null);
                }}
                className="ml-2 rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/30"
                aria-label="Remover arquivo"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <Upload
                className="h-8 w-8 text-[var(--color-muted-foreground)]"
                aria-hidden="true"
              />
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Arraste o arquivo aqui, ou
              </p>
              <Button type="button" variant="ghost" size="sm" onClick={onPickClick}>
                escolher arquivo
              </Button>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                .csv ou .xlsx, até 5 MB
              </p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onInputChange}
            className="hidden"
            data-testid="products-import-file-input"
            aria-label="Selecionar planilha CSV ou XLSX para importação"
          />
        </div>

        {fileError ? (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {fileError}
          </div>
        ) : null}

        {submitError ? (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {submitError}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!file || !!fileError || mutation.isPending}
          >
            {mutation.isPending ? "Importando…" : "Importar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "info" | "muted";
}) {
  const toneClass = {
    success: "border-[var(--color-success)] text-[var(--color-success)]",
    info: "border-[var(--color-primary)] text-[var(--color-primary)]",
    muted: "border-[var(--color-border)] text-[var(--color-muted-foreground)]",
  }[tone];

  return (
    <div
      className={cn(
        "rounded-md border bg-[var(--color-card)] px-3 py-2 text-center",
        toneClass,
      )}
    >
      <p className="font-mono text-2xl tabular-nums">{value}</p>
      <p className="text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
        {label}
      </p>
    </div>
  );
}
